import { getTranslations } from "@/lib/translations";
import { z } from "zod";

import { canUserPublishContent, requireAdmin, requireStaff } from "@/core/auth/admin-guard";
import { createEventService } from "@/core/services/factory";
import {
  ACTIVITY_RECURRENCE_VALUES,
  ACTIVITY_TYPE_VALUES,
  deriveActivityBounds,
  EVENT_CATEGORY_VALUES,
  EVENT_EDIT_STATUS_VALUES,
  getEventPublishFieldErrors,
  getEventContentPublishFieldErrors,
  getEventSharePath,
  isEventEditorialChecklistComplete,
  normalizePonctuelSchedule,
  normalizeRecurrentSchedule,
  parseActivityScheduleFromDb,
  type ActivityRecurrenceValue,
  type ActivityTypeValue,
  type UpdateEventState,
  type UpdateEventTaxonomyResult,
} from "@/core/services/types/event.types";
import {
  eventCalendarDayToEndIso,
  eventCalendarDayToStartIso,
  isEventCalendarDay,
} from "@/lib/event-dates";
import { isArticlePublishStatus } from "@/core/services/types/article.types";
import {
  ARTICLE_COVER_IMAGE_MAX_BYTES,
  isAcceptedCoverImageType,
} from "@/core/services/types/media.types";

function parseFieldErrors(
  issues: z.ZodIssue[],
): Record<string, string> {
  const fieldErrors: Record<string, string> = {};
  for (const issue of issues) {
    const field = issue.path[0];
    if (typeof field === "string" && !fieldErrors[field]) {
      fieldErrors[field] = issue.message;
    }
  }
  return fieldErrors;
}

function normalizeRichText(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (trimmed === "" || trimmed === "<p></p>") return null;
  return value;
}

function normalizeOptionalText(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}

function normalizeOptionalCalendarDay(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (trimmed === "") return null;
  return isEventCalendarDay(trimmed) ? trimmed : null;
}

function mapCoverImageUploadError(
  error: string,
  t: ReturnType<typeof getTranslations>,
): string {
  const normalized = error.toLowerCase();

  if (normalized.includes("row-level security")) {
    return t("coverImageStorageForbidden");
  }

  if (normalized.includes("bucket not found")) {
    return t("coverImageBucketNotFound");
  }

  return t("coverImageUploadFailed");
}

function normalizeOptionalActivityType(value: unknown): ActivityTypeValue | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (trimmed === "") return null;
  return ACTIVITY_TYPE_VALUES.includes(trimmed as ActivityTypeValue)
    ? (trimmed as ActivityTypeValue)
    : null;
}

function parseBooleanField(value: unknown): boolean {
  return value === "true" || value === true;
}

function parseActivityScheduleField(
  value: unknown,
  isRecurrent: boolean,
): ReturnType<typeof parseActivityScheduleFromDb> | null {
  if (typeof value !== "string" || value.trim() === "") return null;

  try {
    const parsed = JSON.parse(value) as unknown;
    return parseActivityScheduleFromDb(parsed, isRecurrent);
  } catch {
    return null;
  }
}

export async function updateEventContent(
  _prevState: UpdateEventState,
  formData: FormData,
): Promise<UpdateEventState> {
  await requireStaff();

  const t = getTranslations("events.edit.validation");

  const idResult = z.uuid().safeParse(formData.get("id"));
  if (!idResult.success) {
    return {
      success: false,
      error: t("fixFields"),
    };
  }

  const events = createEventService();
  const existing = await events.getForEdit(idResult.data);

  if (!existing) {
    return {
      success: false,
      error: t("updateFailed"),
    };
  }

  const isPublished = existing.status === "PUBLISHED";

  const schema = z.object({
    id: z.uuid(),
    title: isPublished
      ? z.string().trim().min(1, t("titleRequired"))
      : z.preprocess(
        (value) => (typeof value === "string" ? value : ""),
        z.string(),
      ),
    description_short: z.preprocess(
      normalizeOptionalText,
      z.string().nullable(),
    ),
    description_long: z.preprocess(
      normalizeRichText,
      z.string().nullable(),
    ),
    start_date: z.preprocess(
      normalizeOptionalCalendarDay,
      z.string().nullable(),
    ),
    end_date: z.preprocess(
      normalizeOptionalCalendarDay,
      z.string().nullable(),
    ),
    event_category: z.enum(EVENT_CATEGORY_VALUES, {
      error: t("categoryRequired"),
    }),
    whart_review_title: z.preprocess(
      normalizeOptionalText,
      z.string().nullable(),
    ),
    whart_review_content: z.preprocess(
      normalizeRichText,
      z.string().nullable(),
    ),
    whart_else: z.preprocess(
      normalizeOptionalText,
      z.string().nullable(),
    ),
    pricing_info: z.preprocess(
      normalizeOptionalText,
      z.string().nullable(),
    ),
    activity_type: z.preprocess(
      normalizeOptionalActivityType,
      z.enum(ACTIVITY_TYPE_VALUES).nullable(),
    ),
    is_family: z.preprocess(parseBooleanField, z.boolean()),
    is_recurrent: z.preprocess(parseBooleanField, z.boolean()),
    activity_schedule_raw: z.preprocess(
      (value) => value,
      z.string().nullable(),
    ),
  });

  const parsed = schema.safeParse({
    id: formData.get("id"),
    title: formData.get("title"),
    description_short: formData.get("description_short"),
    description_long: formData.get("description_long"),
    start_date: formData.get("start_date"),
    end_date: formData.get("end_date"),
    event_category: formData.get("event_category"),
    whart_review_title: formData.get("whart_review_title"),
    whart_review_content: formData.get("whart_review_content"),
    whart_else: formData.get("whart_else"),
    pricing_info: formData.get("pricing_info"),
    activity_type: formData.get("activity_type"),
    is_family: formData.get("is_family"),
    is_recurrent: formData.get("is_recurrent"),
    activity_schedule_raw: formData.get("activity_schedule"),
  });

  if (!parsed.success) {
    return {
      success: false,
      error: t("fixFields"),
      fieldErrors: parseFieldErrors(parsed.error.issues),
    };
  }

  const {
    id,
    event_category,
    start_date: startDateInput,
    end_date: endDateInput,
    activity_schedule_raw,
    is_recurrent: parsedIsRecurrent,
    ...content
  } = parsed.data;

  const start_date = startDateInput
    ? eventCalendarDayToStartIso(startDateInput)
    : null;
  const end_date = endDateInput ? eventCalendarDayToEndIso(endDateInput) : null;

  const activitySchedule = parseActivityScheduleField(
    activity_schedule_raw,
    parsedIsRecurrent,
  );

  let activityFields: {
    activity_type: ActivityTypeValue | null;
    is_family: boolean;
    is_recurrent: boolean;
    activity_schedule: ReturnType<typeof parseActivityScheduleFromDb> | null;
    pricing_info: string | null;
    start_date?: string | null;
    end_date?: string | null;
  };

  if (event_category === "ACTIVITY") {
    const normalizedSchedule = activitySchedule
      ? parsedIsRecurrent
        ? normalizeRecurrentSchedule(
          (activitySchedule as { reccurence: ActivityRecurrenceValue }).reccurence,
          (activitySchedule as { days: number[] }).days,
        )
        : normalizePonctuelSchedule(
          (activitySchedule as { dates: string[] }).dates,
        )
      : parsedIsRecurrent
        ? { reccurence: "Chaque semaine" as ActivityRecurrenceValue, days: [] }
        : { dates: [] };

    const bounds = deriveActivityBounds(parsedIsRecurrent, normalizedSchedule);

    activityFields = {
      activity_type: content.activity_type,
      is_family: content.is_family,
      is_recurrent: parsedIsRecurrent,
      activity_schedule: normalizedSchedule,
      pricing_info: content.pricing_info,
      start_date: start_date ?? bounds?.start_date ?? null,
      end_date: end_date ?? bounds?.end_date ?? null,
    };
  } else {
    activityFields = {
      activity_type: null,
      is_family: false,
      is_recurrent: false,
      activity_schedule: null,
      pricing_info: content.pricing_info,
      ...(event_category !== "PERMANENT_COLLECTION"
        ? { start_date: start_date ?? null, end_date: end_date ?? null }
        : {}),
    };
  }

  if (isPublished) {
    const publishFieldErrors = getEventContentPublishFieldErrors(
      {
        title: content.title,
        place_id: existing.place_id,
        event_category,
        start_date: activityFields.start_date ?? start_date,
        end_date: activityFields.end_date ?? end_date,
        activity_type: activityFields.activity_type,
        is_recurrent: activityFields.is_recurrent,
        activity_schedule: activityFields.activity_schedule,
      },
      {
        titleRequired: t("titleRequired"),
        placeRequired: t("placeRequired"),
        activityTypeRequired: t("activityTypeRequired"),
        activityDatesRequired: t("activityDatesRequired"),
        activityRecurrenceRequired: t("activityRecurrenceRequired"),
        activityDaysRequired: t("activityDaysRequired"),
        startDateRequired: t("startDateRequired"),
        endDateRequired: t("endDateRequired"),
        endDateBeforeStart: t("endDateBeforeStart"),
      },
    );

    if (!content.description_short?.trim()) {
      publishFieldErrors.description_short = t("descriptionShortRequired");
    }

    if (Object.keys(publishFieldErrors).length > 0) {
      return {
        success: false,
        error: t("publishedContentIncomplete"),
        fieldErrors: publishFieldErrors,
      };
    }
  }

  const updated = await events.updateEventContent(id, {
    title: content.title,
    description_short: content.description_short,
    description_long: content.description_long,
    whart_review_title: content.whart_review_title,
    whart_review_content: content.whart_review_content,
    whart_else: content.whart_else,
    event_category,
    ...activityFields,
  });

  if (!updated) {
    return {
      success: false,
      error: t("updateFailed"),
    };
  }
  return { success: true, data: updated };
}

function normalizeOptionalUuid(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}

export async function updateEventStatus(
  _prevState: UpdateEventState,
  formData: FormData,
): Promise<UpdateEventState> {
  const user = await requireStaff();

  const t = getTranslations("events.edit.validation");

  const schema = z.object({
    id: z.uuid(),
    edit_status: z.enum(EVENT_EDIT_STATUS_VALUES),
  });

  const parsed = schema.safeParse({
    id: formData.get("id"),
    edit_status: formData.get("edit_status"),
  });

  if (!parsed.success) {
    return {
      success: false,
      error: t("fixFields"),
      fieldErrors: parseFieldErrors(parsed.error.issues),
    };
  }

  const { id, edit_status } = parsed.data;

  if (isArticlePublishStatus(edit_status) && !canUserPublishContent(user)) {
    return {
      success: false,
      error: t("publishForbidden"),
    };
  }

  const events = createEventService();

  if (isArticlePublishStatus(edit_status)) {
    const event = await events.getForEdit(id);

    if (!event || !isEventEditorialChecklistComplete(event)) {
      return {
        success: false,
        error: t("publishChecklistIncomplete"),
      };
    }

    const publishFieldErrors = getEventPublishFieldErrors(event, {
      titleRequired: t("titleRequired"),
      placeRequired: t("placeRequired"),
      activityTypeRequired: t("activityTypeRequired"),
      activityDatesRequired: t("activityDatesRequired"),
      activityRecurrenceRequired: t("activityRecurrenceRequired"),
      activityDaysRequired: t("activityDaysRequired"),
      startDateRequired: t("startDateRequired"),
      endDateRequired: t("endDateRequired"),
      endDateBeforeStart: t("endDateBeforeStart"),
    });

    if (Object.keys(publishFieldErrors).length > 0) {
      return {
        success: false,
        error: t("publishFieldsIncomplete"),
        fieldErrors: publishFieldErrors,
      };
    }
  }

  const updated = await events.updateEventStatus(id, {
    editStatus: edit_status,
  });

  if (!updated) {
    return {
      success: false,
      error: t("statusUpdateFailed"),
    };
  }
  return { success: true, data: updated };
}

export async function updateEventAssignment(
  _prevState: UpdateEventState,
  formData: FormData,
): Promise<UpdateEventState> {
  await requireStaff();

  const t = getTranslations("events.edit.validation");

  const schema = z.object({
    id: z.uuid(),
    assigned_to: z.preprocess(
      normalizeOptionalUuid,
      z.string().uuid().nullable(),
    ),
  });

  const parsed = schema.safeParse({
    id: formData.get("id"),
    assigned_to: formData.get("assigned_to"),
  });

  if (!parsed.success) {
    return {
      success: false,
      error: t("fixFields"),
      fieldErrors: parseFieldErrors(parsed.error.issues),
    };
  }

  const { id, assigned_to } = parsed.data;
  const events = createEventService();
  const updated = await events.updateEventAssignment(id, {
    assignedTo: assigned_to,
  });

  if (!updated) {
    return {
      success: false,
      error: t("assignmentUpdateFailed"),
    };
  }
  return { success: true, data: updated };
}

export async function updateEventInternalNotes(
  _prevState: UpdateEventState,
  formData: FormData,
): Promise<UpdateEventState> {
  await requireStaff();

  const t = getTranslations("events.edit.validation");

  const schema = z.object({
    id: z.uuid(),
    internal_notes: z.preprocess(
      normalizeOptionalText,
      z.string().nullable(),
    ),
  });

  const parsed = schema.safeParse({
    id: formData.get("id"),
    internal_notes: formData.get("internal_notes"),
  });

  if (!parsed.success) {
    return {
      success: false,
      error: t("fixFields"),
      fieldErrors: parseFieldErrors(parsed.error.issues),
    };
  }

  const { id, internal_notes } = parsed.data;
  const events = createEventService();
  const updated = await events.updateEventInternalNotes(id, {
    internalNotes: internal_notes,
  });

  if (!updated) {
    return {
      success: false,
      error: t("internalNotesUpdateFailed"),
    };
  }
  return { success: true, data: updated };
}

export async function updateEventCoverImage(
  _prevState: UpdateEventState,
  formData: FormData,
): Promise<UpdateEventState> {
  await requireStaff();

  const t = getTranslations("events.edit.validation");

  const schema = z.object({
    id: z.uuid(),
    cover_image_url: z.preprocess(
      (value) =>
        typeof value === "string" && value.trim() === "" ? null : value,
      z.url(t("invalidCoverImageUrl")).nullable(),
    ),
  });

  const parsed = schema.safeParse({
    id: formData.get("id"),
    cover_image_url: formData.get("cover_image_url"),
  });

  if (!parsed.success) {
    return {
      success: false,
      error: t("fixFields"),
      fieldErrors: parseFieldErrors(parsed.error.issues),
    };
  }

  const { id, cover_image_url } = parsed.data;
  const events = createEventService();
  const updated = await events.updateEventCoverImage(id, {
    coverImageUrl: cover_image_url,
  });

  if (!updated) {
    return {
      success: false,
      error: t("coverImageUpdateFailed"),
    };
  }
  return { success: true, data: updated };
}

export async function updateEventPlace(
  _prevState: UpdateEventState,
  formData: FormData,
): Promise<UpdateEventState> {
  await requireStaff();

  const t = getTranslations("events.edit.validation");

  const schema = z.object({
    id: z.uuid(),
    place_id: z.uuid({ message: t("placeRequired") }),
  });

  const parsed = schema.safeParse({
    id: formData.get("id"),
    place_id: formData.get("place_id"),
  });

  if (!parsed.success) {
    return {
      success: false,
      error: t("fixFields"),
      fieldErrors: parseFieldErrors(parsed.error.issues),
    };
  }

  const { id, place_id } = parsed.data;
  const events = createEventService();
  const updated = await events.updateEventPlace(id, { placeId: place_id });

  if (!updated) {
    return {
      success: false,
      error: t("placeUpdateFailed"),
    };
  }
  return { success: true, data: updated };
}

export async function uploadEventCoverImage(
  _prevState: UpdateEventState,
  formData: FormData,
): Promise<UpdateEventState> {
  await requireStaff();

  const t = getTranslations("events.edit.validation");
  const idResult = z.uuid().safeParse(formData.get("id"));
  const file = formData.get("cover_image");

  if (!idResult.success) {
    return {
      success: false,
      error: t("fixFields"),
    };
  }

  if (!(file instanceof File) || file.size === 0) {
    return {
      success: false,
      error: t("coverImageRequired"),
    };
  }

  if (!isAcceptedCoverImageType(file.type)) {
    return {
      success: false,
      error: t("coverImageInvalidType"),
    };
  }

  if (file.size > ARTICLE_COVER_IMAGE_MAX_BYTES) {
    return {
      success: false,
      error: t("coverImageTooLarge"),
    };
  }

  const events = createEventService();
  const result = await events.uploadEventCoverImage(idResult.data, file);

  if (!result.success) {
    return {
      success: false,
      error: mapCoverImageUploadError(result.error, t),
    };
  }
  return { success: true, data: result.data };
}

export async function updateEventTaxonomy(
  _prevState: UpdateEventTaxonomyResult,
  formData: FormData,
): Promise<UpdateEventTaxonomyResult> {
  await requireStaff();

  const t = getTranslations("events.edit.validation");

  const idResult = z.uuid().safeParse(formData.get("id"));

  if (!idResult.success) {
    return { success: false, error: t("fixFields") };
  }

  const uuidArray = z.array(z.uuid());

  const artFormIds = uuidArray.safeParse(formData.getAll("art_form_ids"));
  const artisticPeriodIds = uuidArray.safeParse(formData.getAll("artistic_period_ids"));
  const culturalAreaIds = uuidArray.safeParse(formData.getAll("cultural_area_ids"));
  const exhibitionTypeIds = uuidArray.safeParse(formData.getAll("exhibition_type_ids"));

  if (!artFormIds.success || !artisticPeriodIds.success || !culturalAreaIds.success || !exhibitionTypeIds.success) {
    return { success: false, error: t("fixFields") };
  }

  const events = createEventService();
  const result = await events.updateEventTaxonomy(idResult.data, {
    artFormIds: artFormIds.data,
    artisticPeriodIds: artisticPeriodIds.data,
    culturalAreaIds: culturalAreaIds.data,
    exhibitionTypeIds: exhibitionTypeIds.data,
  });

  if (!result.success) {
    return { success: false, error: t("taxonomyUpdateFailed") };
  }
  return { success: true };
}

export async function deleteEventSignalAction(id: string): Promise<void> {
  await requireAdmin();

  const t = getTranslations("events.edit.statusActions");
  const parsed = z.uuid().safeParse(id);

  if (!parsed.success) {
    throw new Error(t("deleteFailed"));
  }

  const result = await createEventService().deleteSignalContent(parsed.data);

  if (!result.success) {
    throw new Error(
      result.error === "not_deletable" ? t("deleteNotAllowed") : t("deleteFailed"),
    );
  }
}
